<template>
  <div
    class="product-gallery critical-component-clear"
    :class="[
      state.modalIsActive && 'product-gallery--modal-is-active',
    ]"
  >
    <div class="product-gallery__carousel-container">
      <div
        ref="carousel"
        class="product-gallery__carousel splide"
        :js-carousel="galleryNamespace"
      >
        <div class="splide__track">
          <ul class="splide__list">
            <li
              v-for="(image, index) of currentVariantImages"
              :key="`${image.id}-${index}-${image.variant}`"
              class="product-gallery__slide splide__slide"
            >
              <div
                v-if="image.type === 'external'"
                class="
                  product-gallery-zoom__image-container
                  images
                  images--external
                "
              >
                <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
                <img
                  class="
                    product-gallery-zoom__image
                    images__image
                    image--external
                    lazyload
                    lazypreload
                  "
                  src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
                  :data-src="image.src"
                  alt=""
                  role="presentation"
                  aria-hidden="true"
                >
              </div>
              <responsive-image
                v-else
                block-class="product-gallery-zoom"
                :image="image"
                :ratio="isMobile ? '1:1' : false"
                min-max="600-1000"
                :sizes="isMobile ? '100vw' : '50vw'"
              />

              <button
                v-if="showGalleryModalButton"
                class="product-gallery__zoom-button"
                type="button"
                @click.prevent="handleViewModelClick"
                v-text="$string('product.gallery.open_zoom')"
              />
            </li>
          </ul>
        </div>

        <div
          v-if="showArrows"
          class="product-gallery__arrows splide__arrows"
        >
          <transition name="fade">
            <button
              v-show="showPrevArrow"
              class="
                splide__arrow
                splide__arrow--secondary
                splide__arrow--prev
                splide__arrow--transition
              "
              type="button"
              :aria-label="$string('accessibility.carousel.previous')"
            >
              <icon-previous />
            </button>
          </transition>

          <transition name="fade">
            <button
              v-show="showNextArrow"
              class="
                splide__arrow
                splide__arrow--secondary
                splide__arrow--next
                splide__arrow--transition
              "
              type="button"
              :aria-label="$string('accessibility.carousel.next')"
            >
              <icon-next />
            </button>
          </transition>
        </div>
      </div>

      <span
        v-if="badgeText"
        class="product-gallery__badge"
        :class="badgeColor"
        v-text="badgeText"
      />

      <div
        v-if="current3dModel"
        class="product-gallery__controls"
      >
        <button
          class="product-gallery__controls__button"
          type="button"
          @click.prevent="handleView3DModelClick"
          v-text="$string('product.gallery.view_model')"
        />
        <button
          v-if="current3dModel"
          class="product-gallery__controls__button product-gallery__controls__button-ar"
          data-shopify-xr
          data-shopify-xr-hidden
          type="button"
          :data-shopify-model3d-id="current3dModel?.id"
          :data-shopify-title="variant?.title"
          @click.prevent="triggerAR"
          v-text="$string('product.gallery.ar')"
        />

        <button
          v-if="current3dModel"
          class="product-gallery__controls__button product-gallery__controls__button-ar-tab"
          type="button"
          @click.prevent="handleArTabToggle"
          v-text="$string('product.gallery.ar')"
        />
      </div>
    </div>

    <overlay
      key="productGalleryModal"
      block-class="product-gallery-modal"
      :teleport="false"
      :template="false"
      direction="up"
      namespace="productGalleryModal"
      transition="none"
      type="modal"
      @modal-is-active="modalIsActive"
    >
      <template #body="{ isActive }">
        <product-gallery-modal
          v-if="getComponentVisible('product-gallery-modal')"
          v-cloak
          :current-3d-model-markup="current3dModelMarkup"
          :current-3d-model="current3dModel"
          :current-index="state.slideIndex"
          :images="currentVariantImages"
          :is-active="isActive"
          :mode="state.modalMode"
          :models="product.models"
          :variant="variant"
          @gallery-closed="handleGalleryClosed"
          @update-gallery-index="handleZoomGalleryIndexUpdate"
          @update-gallery-mode="handleGalleryModeUpdate"
          @toggle-3d-model="handleView3DModelClick"
          @toggle-gallery="handleGalleryModeUpdate"
          @toggle-ar-tab="handleArTabToggle"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Product gallery (product-gallery)
 * -----------------------------------------------------------------------------
 * Base component for the Product Gallery carousel. Will house the Zoom viewer
 * and 3D Explorer.
 *
 * @param {String} containerContext - Where the component is mounted -
 * 'quick-view/pdp'
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} namespace - Unique namespace for the Gallery.
 * @param {Object|Boolean} product - CANVAS formatted product object.
 * @param {Object|Boolean} variant - CANVAS formatted variant object.
 *
 */
import { defineAsyncComponent, nextTick } from 'vue'
import { mapActions, mapState } from 'vuex'

import Carousel from '~/api/carousel'

import Overlay from '~global/overlay/overlay'
import ResponsiveImage from '~global/images/responsive-image'

import IconPrevious from '~icons/direction/arrow/left.svg'
import IconNext from '~icons/direction/arrow/right.svg'

export default {
  name: 'ProductGallery',

  components: {
    IconNext,
    IconPrevious,
    Overlay,
    ProductGalleryModal: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.product-gallery-modal' */
        '~async/product-gallery-modal/product-gallery-modal'
      ),
    }),
    ResponsiveImage,
  },

  props: {
    containerContext: {
      type: String,
      default: 'pdp',
    },
    // liquid: {
    //   type: Object,
    //   default: () => ({
    //     sectionId: '',
    //   }),
    // },
    namespace: {
      type: String,
      default: 'ProductGallery',
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variantImageData: {
      type: [Boolean, Object],
      default: false,
    },
    variantModelData: {
      type: [Boolean, Object],
      default: false,
    },
    variantStatusBadgeData: {
      type: [Boolean, Object],
      default: false,
    },
  },

  data() {
    return {
      carousel: false,
      carouselConfig: {
        arrows: false,
        classes: {
          pagination: 'splide__pagination splide__pagination--light product-gallery__pagination',
          page: 'splide__pagination__page product-gallery__page',
        },
        pagination: true,
        rewind: false,
        speed: 800,
        type: 'slide',
      },
      state: {
        modalIsActive: false,
        modalMode: 'zoom',
        slideIndex: 0,
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      screenHeight: (state) => state.index.screen.height,
      screenWidth: (state) => state.index.screen.width,
    }),

    /**
     * Calculate a unique namespace for the Gallery.
     * @returns {String}
     */
    galleryNamespace() {
      return `${this.namespace}-Gallery`
    },

    /**
     * Calculate whether to show carousel arrows.
     * - Don't show if there's only one image
     * @returns {Boolean}
     */
    showArrows() {
      return this.currentVariantImages.length > 1
    },

    /**
     * Calculate whether to show previous arrow button.
     * - Don't show if on first slide
     * @returns {Boolean}
     */
    showPrevArrow() {
      return this.state.slideIndex !== 0
    },

    /**
     * Calculate whether to show next arrow button.
     * - Don't show if on last slide
     * @returns {Boolean}
     */
    showNextArrow() {
      return this.state.slideIndex !== (this.currentVariantImages.length - 1)
    },

    /**
     * Calculate if images exist and if to show the gallery.
     * @returns {Boolean}
     */
    showGallery() {
      return Boolean(this.product?.images && this.product.images.length)
    },

    /**
     * Calculate if to allow the opening of the 'Zoom modal'
     * @returns {Boolean}
     */
    showGalleryModalButton() {
      return this.containerContext === 'pdp'
    },

    /**
     * Compute current variant's specific badge from variant metafield.
     * @returns {String}
     */
    currentVariantStatusBadge() {
      if (!this.variant) {
        return ''
      }

      return this.variantStatusBadgeData?.[this.variant.id]
    },

    /**
     * Compute badge text from variant metafield.
     * @returns {String}
     */
    badgeText() {
      if (!this.currentVariantStatusBadge) {
        return ''
      }

      return this.currentVariantStatusBadge.split(':')[1]
    },

    /**
     * Compute 3d model for current variant.
     * @returns {String}
     */
    current3dModel() {
      if (!this.variant) {
        return false
      }

      if(!this.variantModelData) {
        return false
      }

      return this.variantModelData[this.variant.id]
    },

    /**
     * Compute 3d model viewer markup for current variant.
     * @returns {String}
     */
    current3dModelMarkup() {
      if (!this.variant) {
        return false
      }

      if(!this.variantModelData) {
        return false
      }

      const modelMarkup = this.variantModelData[this.variant.id]

      if (!modelMarkup) {
        return false
      }

      const doc = new DOMParser().parseFromString(modelMarkup.markup, 'text/html')
      return doc.documentElement.textContent
    },

    /**
     * Compute badge color from variant metafield.
     * @returns {String}
     */
    badgeColor() {
      if (!this.currentVariantStatusBadge) {
        return ''
      }

      const badgeColorInformation = this.currentVariantStatusBadge.split(':')[0]

      return `product-gallery__badge--${badgeColorInformation.replace('badge-', '')}`
    },

    /**
     * Compute current variant images.
     * @returns {Array}
     */
    currentVariantImages() {
      if (!this.variant) {
        return []
      }

      const variantImages = []

      const shopifyVariantImage = this.product.images.find((image) => {
        if (!this.variant.image) {
          return this.product.images
        }

        return image.id === this.variant.image
      })

      if (!shopifyVariantImage) {
        return []
      }

      variantImages.push(shopifyVariantImage)

      /**
       * Get additional images from metafields.
       */
      const metafieldImagesArray = this.variantImageData?.[this.variant.id]

      if (metafieldImagesArray) {
        metafieldImagesArray
          .filter(Boolean)
          .forEach((image) => {
            const imageObj = {
              id: image,
              src: image,
            }
            if (!image.includes('https://cdn.shopify.com')) {
              imageObj.type = 'external'
            }
            variantImages.push(imageObj)
          })
      }

      return variantImages
    },
  },

  watch: {

    /**
     * Watch the device state - if mobile device or not.
     * - Update the carousel options with arrow visibility.
     */
    isMobile() {
      if (!this.carousel) {
        return
      }

      this.carousel.options = {
        arrows: this.showArrows,
      }
    },

    /**
     * Watch the active variant.
     * - Indicates that the PDP form has updated.
     */
    variant: {
      handler() {
        if (
          !this.variant ||
          !this.$refs.carousel ||
          !this.product?.images?.length) {
          return
        }

        /**
         * Reset active carousel when changing.
         */
        if (typeof this.carousel !== 'boolean') {
          this.carousel.destroy()
          this.carousel = false
        }

        this.initCarousel()

        nextTick().then(() => {
          this.state.slideIndex = 0
        })
      },
      deep: true,
    },
  },

  mounted() {
    this.$nextTick().then(() => {
      this.loadComponent('product-gallery-modal')
    })
  },

  beforeUnmount() {

    /**
     * Before unmount, destroy the Splide instance.
     * - Required for toggling between Quick View.
     */
    if (typeof this.carousel === 'boolean') {
      return
    }

    this.carousel.destroy()
    this.carousel = false
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      loadComponent: 'components/load',
    }),

    /**
     * Set Event Listeners.
     */
    setEventListeners() {

      /**
       * The carousel has just finished transitioning between slides.
       */
      this.carousel.on('move', () => {
        this.state.slideIndex = this.carousel.index
      })
    },

    /**
     * Handle gallery modal being closed.
     */
    handleGalleryClosed() {
      if (!this.carousel) {
        return
      }

      this.carousel.go(this.state.slideIndex)
      this.state.modalIsActive = false

      setTimeout(() => {
        this.carousel.refresh()
      }, 0)
    },

    /**
     * Respond to events for when the mode of the gallery modal has changed.
     */
    handleGalleryModeUpdate(payload = 'zoom') {
      this.state.modalMode = payload
    },

    /**
     * The user has modified the active slide in the 'Zoom modal'.
     * - Update the index in the inline gallery to keep in sync.
     */
    handleZoomGalleryIndexUpdate(index) {
      if (!this.carousel) {
        return
      }

      this.state.slideIndex = index
    },

    /**
     * The button to trigger model.
     * @param {String|Number} index - Array index of image to default model to.
     */
    handleViewModelClick() {
      const modalMode = 'zoom'

      this.state.modalMode = modalMode

      this.openOverlay({
        component: 'product-gallery-modal',
        ignoreDismissed: true,
        namespace: 'productGalleryModal',
      })
    },

    /**
     * The button to trigger the QR code tab on devices that don't support AR directly.
     * @param {String|Number} index - Array index of image to default model to.
     */
    handleArTabToggle() {
      this.state.modalMode = 'ar-tab'
      this.openOverlay({
        component: 'product-gallery-ar-tab',
        ignoreDismissed: true,
        namespace: 'productGalleryModal',
      })
    },

    /**
     * The button to trigger 3D model.
     */
    handleView3DModelClick() {
      const modalMode = '3d'

      this.state.modalMode = modalMode
      this.openOverlay({
        component: 'product-gallery-modal',
        ignoreDismissed: true,
        namespace: 'productGalleryModal',
      })
    },

    /**
     * Trigger Shopify's native AR experience.
     */
    triggerAR() {
      if(!this.current3dModel) {
        return
      }

      window.ShopifyXR.launchXR({
        model3dId: this.current3dModel.id,
        title: this.variant.title,
      })
    },

    /**
     * Initialise carousel.
     */
    initCarousel() {
      this.carousel = Carousel(`[js-carousel="${this.galleryNamespace}"]`, {
        ...this.carouselConfig,
        arrows: this.showArrows,
      })

      nextTick().then(() => {
        this.carousel.mount()
        this.setEventListeners()
      })
    },

    /**
     * Keep track of modal active state.
     */
    modalIsActive() {
      this.state.modalIsActive = true
    },
  },
}
</script>

<style lang="scss">
@import './product-gallery';
</style>
